/**
 * English messages for the launch screens converted in C7-REST: the
 * Organization, Finance, Sales, Procurement, Inventory and Setup screens that
 * no design slice rebuilds (the document editor rebuilds the nine document
 * lists, the CRM design the leads, opportunities and quotations).
 *
 * Spread into `en` (./en.ts), so these keys are `MessageKey`s like any other
 * and the catalog stays one i18next resource bundle. The English is the
 * English each screen had before it was converted; the rules of ./en.ts
 * apply (placeholders for every number, amount and date; plurals by suffix).
 *
 * - `fields`: column and field labels several screens share, so one concept
 *   has one word on every screen (docs/help/glossary.md, "One concept, one
 *   word").
 * - `values`: words for codes the API stores (an account type, a movement
 *   type, a valuation method…), read through `valueLabel` in ../records.ts;
 *   never the raw value.
 */
export const screensEn = {
  fields: {
    name: "Name",
    code: "Code",
    status: "Status",
    number: "Number",
    date: "Date",
    type: "Type",
    currency: "Currency",
    customer: "Customer",
    supplier: "Supplier",
    product: "Product",
    warehouse: "Warehouse",
    sku: "SKU",
    quantity: "Quantity",
    qty: "Qty",
    total: "Total",
    amount: "Amount",
    period: "Period",
    actions: "Actions",
    description: "Description",
    source: "Source",
    reason: "Reason",
    requestedBy: "Requested by",
    requested: "Requested",
    received: "Received",
    version: "Version",
    title: "Title",
    account: "Account",
    debit: "Debit",
    credit: "Credit",
    lot: "Lot",
    email: "Email",
    company: "Company",
    branch: "Branch",
    legalEntity: "Legal entity",
    address: "Address",
    timezone: "Timezone",
    country: "Country",
    created: "Created",
    risk: "Risk",
    category: "Category",
    module: "Module",
    entity: "Entity",
    event: "Event",
    severity: "Severity",
    outcome: "Outcome",
    ip: "IP",
    device: "Device",
    lastActive: "Last active",
    from: "From",
    to: "To",
    active: "Active",
    balance: "Balance",
    taxId: "Tax ID",
    /** PAY-ALLOC: what a payment still holds on account. */
    unallocated: "Unallocated",
  },
  shared: {
    /** Joins the words of a list on one line. */
    listSeparator: ", ",
    yes: "Yes",
    no: "No",
    export: "Export",
    previous: "Previous",
    next: "Next",
    /** The time zone a record has when none is set. */
    utc: "UTC",
    /** A version number, as a badge: "v3". */
    versionNumber: "v{{version}}",
  },
  values: {
    accountType: {
      Asset: "Asset",
      Liability: "Liability",
      Equity: "Equity",
      Revenue: "Revenue",
      Expense: "Expense",
    },
    normalBalance: {
      Debit: "Debit",
      Credit: "Credit",
    },
    productType: {
      Item: "Item",
      Service: "Service",
      Expense: "Expense",
    },
    customerType: {
      Individual: "Individual",
      Company: "Company",
    },
    movementType: {
      Receipt: "Receipt",
      Issue: "Issue",
      Transfer: "Transfer",
      Adjustment: "Adjustment",
      Reservation: "Reservation",
      CycleCount: "Cycle count",
      Scrap: "Scrap",
    },
    valuationMethod: {
      Fifo: "FIFO",
      MovingAverage: "Moving average",
      WeightedAverage: "Weighted average",
      Standard: "Standard",
    },
    taxType: {
      VAT: "VAT",
      SalesTax: "Sales tax",
      WithholdingTax: "Withholding tax",
      Excise: "Excise",
      Customs: "Customs",
    },
    risk: {
      Low: "Low",
      Medium: "Medium",
      High: "High",
      Critical: "Critical",
    },
    severity: {
      Info: "Info",
      Warning: "Warning",
      Critical: "Critical",
    },
    outcome: {
      Success: "Success",
      Failure: "Failure",
      Blocked: "Blocked",
    },
    actorType: {
      User: "User",
      ApiClient: "API client",
      System: "System",
      BreakGlass: "Break-glass",
    },
    qualityStatus: {
      Passed: "Passed",
      Quarantined: "Quarantined",
      Failed: "Failed",
    },
    storageType: {
      Relational: "Relational",
      Jsonb: "JSONB",
    },
    viewType: {
      List: "List",
      Kanban: "Kanban",
      Calendar: "Calendar",
      Pivot: "Pivot",
      Tree: "Tree",
      Map: "Map",
    },
    layoutType: {
      Form: "Form",
      Wizard: "Wizard",
      Compact: "Compact",
    },
    fieldType: {
      Text: "Text",
      LongText: "LongText",
      RichText: "RichText",
      Number: "Number",
      Currency: "Currency",
      Percentage: "Percentage",
      Date: "Date",
      DateTime: "DateTime",
      Boolean: "Boolean",
      Select: "Select",
      MultiSelect: "MultiSelect",
      Relation: "Relation",
      User: "User",
      Employee: "Employee",
      Company: "Company",
      Attachment: "Attachment",
      Formula: "Formula (AST-compiled DSL — no eval, no I/O)",
      JSON: "JSON",
    },
    planTier: {
      Starter: "Starter",
      SmallBusiness: "Small Business",
      Professional: "Professional",
      Enterprise: "Enterprise",
      EnterprisePlus: "Enterprise Plus",
    },
    isolationMode: {
      SharedSchema: "Shared schema",
      DedicatedSchema: "Dedicated schema",
      DedicatedCluster: "Dedicated cluster",
    },
    environment: {
      Sandbox: "Sandbox",
      Production: "Production",
    },
    /** System roles, by their code (Role.code, is_system). */
    systemRole: {
      administrator: "Administrator",
      auditor: "Auditor",
      credit_controller: "Credit Controller",
      employee: "Employee",
      finance_manager: "Finance Manager",
      financial_controller: "Financial Controller",
      governance_officer: "Governance Officer",
      hr_manager: "HR Manager",
      manufacturing_manager: "Manufacturing Manager",
      owner: "Owner",
      payables_approver: "Payables Approver",
      project_manager: "Project Manager",
      sales_manager: "Sales Manager",
      supply_chain_manager: "Supply Chain Manager",
    },
  },

  // ---- Organization ---------------------------------------------------------
  org: {
    section: "Organization",
    /** OrgListPage, the scaffold of the four list screens. */
    list: {
      codeHint: "Short identifier used on documents and in reports.",
    },
    structure: {
      title: "Organization structure",
      subtitle:
        "Legal entities, the companies they own, and the branches, departments and locations beneath them.",
      manage: "Manage legal entities",
      emptyTitle: "No legal entity yet",
      emptyMessage:
        "A legal entity is the top of the hierarchy: it owns the companies that own the branches, and it is what journal entries and fiscal years hang from. Provisioning creates the first one; add more for each registered company in the group.",
      add: "Add a legal entity",
      hierarchy: "Hierarchy",
      noCompany: "No company under this entity yet.",
      departments_one: "{{count}} department",
      departments_other: "{{count}} departments",
      branches_one: "{{count}} branch",
      branches_other: "{{count}} branches",
      locations_one: "{{count}} location",
      locations_other: "{{count}} locations",
      orphansTitle: "Companies with no matching legal entity",
      orphansBody:
        "These reference a legal entity that is not visible. They are shown here rather than dropped from the tree, because a company that belongs to nothing cannot post.",
    },
    legalEntities: {
      title: "Legal entities",
      subtitle:
        "Registered companies in the group. Each owns its own ledger, fiscal calendar and chart of accounts.",
      new: "New legal entity",
      nameHint: "How it appears throughout the application.",
      legalName: "Registered legal name",
      legalNameHint: "Defaults to the name above when left blank.",
      country: "Country of registration",
      currency: "Functional currency",
      currencyHint:
        "The currency this entity keeps its books in. It cannot be changed once entries are posted.",
      taxId: "Tax registration number",
      creating: "Creating…",
      create: "Create legal entity",
      loadFailed: "Could not load legal entities",
      emptyTitle: "No legal entities",
      emptyMessage:
        "Provisioning creates the first legal entity from the workspace country. Add one per registered company in the group.",
      registeredName: "Registered name",
    },
    legalEntity: {
      notFound: "Legal entity not found",
      archived: "It may have been archived.",
      registeredAs: "Registered as {{name}}",
      deactivate: "Deactivate",
      deactivating: "Deactivating…",
      activate: "Activate",
      activating: "Activating…",
      confirmDeactivate:
        "Deactivate {{name}}?\n\nNo journal entry, invoice or stock movement can be posted to this entity while it is inactive. Existing records are unaffected.",
      overview: "Overview",
      companiesTab: "Companies ({{count}})",
      registration: "Registration",
      taxRegistration: "Tax registration",
      companiesTitle: "Companies under this entity",
      noCompanies: "No companies",
      noCompaniesMessage:
        "A company is the operating unit beneath a legal entity — it is what branches, departments and day-to-day documents belong to.",
      addCompany: "Add a company",
    },
    companies: {
      title: "Companies",
      subtitle:
        "Operating units. Each belongs to one legal entity and inherits its ledger and currency.",
      new: "New company",
      emptyTitle: "No companies",
      emptyMessage:
        "A company is what branches, departments and documents attach to. Provisioning creates one; add more as the group grows.",
      missingParent:
        "A company must belong to a legal entity, and none exists yet. Create a legal entity first — it carries the currency and country this company will inherit.",
    },
    company: {
      notFound: "Company not found",
      partOf: "Part of {{name}}",
      overview: "Overview",
      branchesTab: "Branches ({{count}})",
      departmentsTab: "Departments ({{count}})",
      details: "Details",
      parentMissing: "Parent entity not found",
      singleSite: "This company operates from a single site.",
      departmentsHint:
        "Departments drive cost attribution and approval routing.",
    },
    branches: {
      title: "Branches",
      subtitle:
        "Sites under a company. A branch carries its own timezone, which drives document dating and SLA clocks.",
      new: "New branch",
      emptyTitle: "No branches",
      emptyMessage:
        "Branches separate reporting and stock by site. A single-site business does not need one.",
      missingParent:
        "A branch belongs to a company, and none exists yet. Create a company first.",
      timezoneHint: "IANA name. Defaults to UTC when left blank.",
    },
    departments: {
      title: "Departments",
      subtitle:
        "The reporting tree inside a company. Departments drive cost attribution and who an approval routes to.",
      new: "New department",
      emptyTitle: "No departments",
      emptyMessage:
        "Departments are how expenses and approvals get attributed to a part of the business.",
      missingParent:
        "A department belongs to a company, and none exists yet. Create a company first.",
      reportsTo: "Reports to",
      topLevel: "Top level",
    },
    locations: {
      title: "Locations",
      subtitle:
        "Addressable places within a branch — floors, offices, loading docks.",
      new: "New location",
      emptyTitle: "No locations",
      emptyMessage:
        "Locations subdivide a branch. Warehouse storage bins are managed under Inventory instead, where the stock context is.",
      missingParent:
        "A location sits inside a branch, and none exists yet. Create a branch first.",
    },
  },

  // ---- Finance --------------------------------------------------------------
  finance: {
    /**
     * The period-close checks (GET /v1/finance/dashboard and
     * /v1/finance/period-close `checklist[].item`). The API sends English text
     * and no code (finance-overview.controller.ts), so they are matched by
     * exact text (features/finance/close-text.ts); anything else is shown as
     * sent.
     */
    closeChecks: {
      allEntriesPosted: "All journal entries posted",
      supplierInvoicesPosted: "Supplier invoices posted",
      customerInvoicesPosted: "Customer invoices posted",
      noOverdueReceivables: "No overdue receivables",
      /** The blocked check's `message`: "3 still unposted". */
      stillUnposted: "{{count}} still unposted",
      awaitingPosting: "{{count}} awaiting posting",
      overdue: "{{count}} overdue",
    },
    dashboard: {
      title: "Financial Dashboard",
      subtitle: "Cash position, payables, receivables, and close readiness.",
      cashPosition: "Cash position",
      accountsPayable: "Accounts payable",
      accountsReceivable: "Accounts receivable",
      fxGainLoss: "FX gain/loss",
      recentEntries: "Recent journal entries",
      narration: "Narration",
      checklist: "Period close checklist",
    },
    accounts: {
      title: "Chart of Accounts",
      subtitle: "Accounts, types, normal balances, and posting flags.",
      import: "Import",
      new: "New account",
      accountCode: "Account code",
      list: "Accounts",
      normalBalance: "Normal balance",
      postable: "Postable",
      inactive: "Inactive",
    },
    ledger: {
      title: "General Ledger",
      subtitle:
        "Posted ledger lines, newest first, by account and date. Each amount is in its own books' currency.",
      allAccounts: "All accounts",
      chosenAccount: "The chosen account",
      accountsNotLoaded:
        "The list of accounts could not be loaded, so it cannot be chosen from here.",
      accountsCutOff:
        "Only the first {{shown}} of {{total}} accounts are listed here. Open an account from the trial balance to see one that is not listed.",
      entityNumber: "Legal entity {{number}}",
      list: "Ledger entries",
      entry: "Entry",
      runningBalanceNotShown:
        "The running balance and the opening balance are not shown yet.",
    },
    trialBalance: {
      title: "Trial Balance",
      subtitle:
        "Each account's debits and credits, and its balance on its side, up to the date you choose.",
      asOf: "As of date",
      asOfHint:
        "Leave it empty to include every posted entry, with no date limit.",
      debits: "Debits",
      credits: "Credits",
      debitBalance: "Debit balance",
      creditBalance: "Credit balance",
      totalDebitBalances: "Total debit balances",
      totalCreditBalances: "Total credit balances",
      openingNotShown:
        "Opening balances and the movement for a period are not shown yet.",
      list: "Trial balance",
      balanced: "Balanced",
      outOfBalance: "Out of balance",
      totalDebits: "Total debits",
      totalCredits: "Total credits",
    },
    statements: {
      title: "Financial Statements",
      subtitle: "P&L, balance sheet and cash flow with period comparison.",
      compare: "Compare period",
      tabs: {
        ProfitAndLoss: "Profit & Loss",
        BalanceSheet: "Balance Sheet",
        CashFlow: "Cash Flow",
      },
      /** `period` as the API sends it for a statement with no period chosen. */
      toDate: "To date",
      /**
       * REP-PL: the Profit & Loss with no period chosen covers every posted
       * entry, whatever its date; "To date" would not be true of it.
       */
      allDates: "All dates",
      allDatesNote:
        "Every posted entry, whatever its date, including any dated after today. Choosing a period arrives with the statement engine.",
      /** Level-0 lines: one per account type (`code` is the type). */
      sections: {
        Asset: "Asset accounts",
        Liability: "Liability accounts",
        Equity: "Equity accounts",
        Revenue: "Revenue accounts",
        Expense: "Expense accounts",
      },
    },
    /**
     * REP-CASH: a figure or report the API says is not available yet
     * (CAPABILITY_NOT_BUILT), by its capability code; `slice` is the build
     * slice the API names.
     */
    notAvailable: {
      title: "Not available yet",
      cashFlowStatement:
        "{{product}} does not yet know which accounts hold cash, so it shows no cash flow rather than a wrong one. The cash flow statement arrives with the statement engine ({{slice}}).",
      cashPosition:
        "No figure is shown until it can be right. It arrives with the statement engine's cash flow ({{slice}}).",
      other: "This arrives with {{slice}}.",
    },
    bankRec: {
      title: "Bank Reconciliation",
      subtitle: "Upload statements, match payments, post reconciliation.",
      upload: "Upload statement",
      list: "Statements",
      statement: "Statement",
      bankAccount: "Bank account",
      matched: "Matched",
      unmatchedColumn: "Unmatched",
    },
    cash: {
      title: "Cash Management",
      subtitle: "Cash position forecast, bank balances, and movements.",
      forecast: "Forecast balance",
      bankBalances: "Bank balances",
      bankAccount: "Bank account",
      movements: "Recent cash movements",
      in: "In",
      out: "Out",
    },
    assets: {
      title: "Fixed Assets",
      subtitle:
        "Asset register with depreciation schedule, acquisition, disposal and impairment.",
      new: "New asset",
      list: "Assets",
      acquired: "Acquired",
      cost: "Cost",
      netBookValue: "Net book value",
    },
    tax: {
      title: "Tax Dashboard",
      subtitle:
        "Tax payable and receivable, and the tax returns recorded for each period.",
      payable: "Tax payable",
      receivable: "Tax receivable",
      returns: "Returns & filings",
      totalNotShown:
        "Not shown: no posting fills the tax ledger this figure is read from yet, so it would read 0 whatever tax has been posted.",
      returnFiguresNotShown:
        "A return's due date and amount are not shown: neither is worked out yet.",
    },
    periodClose: {
      title: "Period Close — {{period}}",
      range: "{{start}} → {{end}}",
      reopen: "Reopen period",
      close: "Close period",
      checklist: "Close checklist",
      blockedTitle: "Why is the period blocked from closing?",
      resolve: "Resolve “{{item}}” before closing the period.",
    },
    rates: {
      title: "Exchange Rates",
      subtitle: "Manual entry or import; lock rates for reporting periods.",
      revalue: "Run revaluation",
      list: "Rates",
      rate: "Rate",
      effectiveFrom: "Effective from",
      effectiveTo: "Effective to",
      locked: "Locked",
      lockedYes: "🔒 Yes",
    },
    budgets: {
      title: "Budgeting",
      subtitle: "Budget editor, versions, and budget-vs-actual reporting.",
      new: "New budget",
      list: "Budgets",
      fiscalYear: "Fiscal year",
      budget: "Budget",
      actual: "Actual",
    },
    consolidation: {
      title: "Intercompany & Consolidation",
      subtitle:
        "Mapping rules, eliminations, currency translation, and consolidation runs.",
      run: "Run consolidation",
      openItems: "Intercompany open items",
      openCount: "{{count}} open",
      counterparty: "Counterparty",
      document: "Document",
      groups: "Consolidation groups",
      entities: "Entities",
      lastRun: "Last run",
    },
  },

  // ---- Sales ----------------------------------------------------------------
  sales: {
    customers: {
      title: "Customers",
      subtitle:
        "Customer master with balances, credit limits, and transaction history.",
      new: "New customer",
      customerNumber: "Customer number",
      list: "Customers",
      creditLimit: "Credit limit",
      openBalance: "Open balance",
    },
    deliveryNotes: {
      title: "Delivery Notes",
      subtitle: "Pick, pack, ship, and handle backorders and returns.",
      new: "New delivery note",
      list: "Delivery notes",
      order: "Order",
      lines: "Lines",
    },
    returns: {
      title: "Returns / RMAs",
      subtitle:
        "Return requests, approvals, receipts, replacement or credit note.",
      new: "New return request",
      list: "Returns",
      sourceInvoice: "Source invoice",
      refund: "Refund",
    },
    priceLists: {
      title: "Price Lists & Discounts",
      subtitle:
        "Price list editor, pricing rules, and discount approval thresholds.",
      new: "New price list",
      list: "Price lists",
      items: "Items",
      discounts: "Discount rules",
      discount: "Discount",
      minAmount: "Min amount",
      approvalThreshold: "Approval threshold",
    },
    creditReview: {
      title: "Credit Limit Review",
      subtitle: "Pending credit decisions with customer risk and exposure.",
      approveSelected: "Approve selected",
      list: "Pending decisions",
      currentLimit: "Current limit",
      requestedLimit: "Requested",
      openAr: "Open AR",
    },
    commissions: {
      title: "Commission Dashboard",
      subtitle: "Sales agent commissions and payout status.",
      list: "Commissions",
      agent: "Agent",
      earned: "Earned",
      paid: "Paid",
    },
  },

  // ---- Procurement ----------------------------------------------------------
  procurement: {
    requests: {
      title: "Purchase Requests",
      subtitle:
        "Internal requests for goods/services; budget-holder approval; convert to purchase order.",
      new: "New purchase request",
      list: "Purchase requests",
      department: "Department",
      expected: "Expected",
    },
    rfqs: {
      title: "RFQs",
      subtitle:
        "Request for quotation: supplier lines, quote collection, and award.",
      new: "New RFQ",
      list: "RFQs",
      suppliers: "Suppliers",
      quotes: "Quotes",
      issued: "Issued",
      deadline: "Deadline",
    },
    suppliers: {
      title: "Suppliers",
      subtitle:
        "Supplier master data: tax ID, bank, status, performance, contracts.",
      new: "New supplier",
      supplierNumber: "Supplier number",
      list: "Suppliers",
      terms: "Terms",
      openPayables: "Open payables",
    },
    evaluations: {
      title: "Supplier Evaluation",
      subtitle: "Scorecards: on-time delivery, quality, price variance, risk.",
      list: "Evaluations",
      onTime: "On-time",
      quality: "Quality",
      priceVariance: "Price variance",
      overall: "Overall",
    },
    contracts: {
      title: "Procurement Contracts",
      subtitle: "Contract terms, renewal dates, and linked purchase orders.",
      new: "New contract",
      list: "Contracts",
      start: "Start",
      end: "End",
      linkedPos: "Linked POs",
    },
  },

  // ---- Inventory ------------------------------------------------------------
  inventory: {
    products: {
      title: "Products",
      subtitle:
        "Product master: variants, UoM, valuation method, tax, status, barcode.",
      new: "New product",
      list: "Products",
      unitOfMeasure: "Unit of measure",
      uom: "UoM",
      valuation: "Valuation",
      barcode: "Barcode",
      variants: "Variants",
      /** The create form's type choices. */
      typeOptions: {
        Item: "Item — stocked goods",
        Service: "Service — no stock",
        Expense: "Expense",
      },
    },
    stock: {
      title: "Stock by Location",
      subtitle:
        "On-hand, reserved, and available quantities by warehouse and lot.",
      allWarehouses: "All warehouses",
      list: "Balances",
      onHand: "On hand",
      reserved: "Reserved",
      available: "Available",
    },
    movements: {
      title: "Inventory Movements",
      subtitle:
        "Immutable movement ledger: receipts, issues, transfers, adjustments, scrap.",
      new: "New movement",
      list: "Movements",
      value: "Value",
      posted: "Posted",
    },
    transfers: {
      title: "Transfers",
      subtitle:
        "Inter-warehouse stock transfers with ship/receive and in-transit tracking.",
      new: "New transfer",
      list: "Transfers",
      shipped: "Shipped",
    },
    adjustments: {
      title: "Adjustments",
      subtitle:
        "Stock adjustments with reason, approval, and immutable posting.",
      new: "New adjustment",
      awaiting: "Adjustments awaiting approval",
      exceeds:
        "Adjustment {{number}} exceeds the configured inventory adjustment threshold.",
      review:
        "Review the adjustment and approve or reject from the approval queue.",
      list: "Adjustments",
      delta: "Delta",
    },
    cycleCounts: {
      title: "Cycle Counts",
      subtitle: "Count sheets, variance capture, approval, and posting.",
      start: "Start count",
      list: "Cycle counts",
      system: "System",
      counted: "Counted",
      variance: "Variance",
      countedBy: "Counted by",
    },
    traceability: {
      title: "Lot / Serial Traceability",
      subtitle:
        "Genealogy of lots and serials: sources, movements, quality status.",
      searchPlaceholder: "Search lot or serial…",
      searchLabel: "Search traceability",
      list: "Traceability records",
      serial: "Serial",
      quality: "Quality",
    },
    reorder: {
      title: "Reorder Planner",
      subtitle:
        "Min/max-based replenishment suggestions with planned purchase orders.",
      generate: "Generate POs",
      belowTitle: "Items below minimum without a planned PO",
      below:
        "{{sku}} in {{warehouse}} is below its minimum level (on hand {{onHand}} < min {{min}}).",
      belowFix: "Generate planned purchase orders for these items.",
      list: "Recommendations",
      onHand: "On hand",
      min: "Min",
      max: "Max",
      suggested: "Suggested",
      plannedPo: "Planned PO",
      exists: "Exists",
      missing: "Missing",
    },
    valuation: {
      title: "Valuation Report",
      subtitle: "Stock on hand now and its value, by product and warehouse.",
      nowOnly:
        "This is the stock as it is now. A valuation at a past date is not available yet.",
      totalAllPages: "Total value, every page",
      list: "Valuation",
      method: "Method",
      unitCost: "Unit cost",
      totalValue: "Total value",
    },
  },

  // ---- Setup ----------------------------------------------------------------
  admin: {
    wizard: {
      title: "Implementation Wizard",
      subtitle: "Guided by the system — every step validates live.",
      empty: "No implementation phases are available for this workspace yet.",
      phases: "Phases",
      step: "{{number}}. {{label}}",
      blocked: "blocked",
      phaseTitle: "Phase {{number}}: {{label}}",
      whyBlocked: "Why is this blocked?",
      /** GET /v1/implementation/phases `phases[].key`. */
      phaseNames: {
        Organization: "Organization Structure",
        Finance: "Financial Configuration",
        Tax: "Tax Configuration",
        UsersRoles: "Users & Roles",
        Approvals: "Approval Workflows",
        MasterData: "Master Data",
        OpeningBalances: "Opening Balances",
        Integrations: "Integrations",
        Testing: "Testing & UAT",
        GoLive: "Go-Live",
      },
      /** `phases[].notices[].code` (workspace-setup.controller.ts). */
      notices: {
        SETUP_NO_COMPANY: "No company has been created yet.",
        SETUP_NO_ACCOUNTS: "The chart of accounts is empty.",
        SETUP_NO_TAX_CODES: "No tax codes are defined.",
        SETUP_NO_MEMBERS: "No members have been added.",
        SETUP_NO_APPROVAL_MATRIX: "No approval matrix has been configured.",
        SETUP_NO_PRODUCTS: "No products or services have been added.",
        SETUP_NO_OPENING_ENTRIES:
          "No opening journal entries have been posted.",
        SETUP_NO_INTEGRATIONS:
          "No integrations are configured. This phase is optional.",
        SETUP_NO_TEST_TRANSACTIONS:
          "No transactions have been entered to test against.",
        SETUP_NOT_LIVE: "The workspace is not live yet.",
        SETUP_WAITING_ON: "Waiting on {{phase}}.",
        SETUP_PHASE_COMPLETE: "{{phase}} is complete.",
      },
    },
    auditLog: {
      title: "Audit log",
      subtitle:
        "Append-only security and access events. Tenant admins cannot modify or purge.",
      eventFilter: "Filter by event type",
      allSeverities: "All severities",
      when: "When",
      actorType: "Actor type",
      target: "Target",
      targetId: "Target ID",
    },
    apiWebhooks: {
      title: "API / Webhook Admin",
      subtitle:
        "API keys, webhook subscriptions, delivery logs, and the dead-letter queue.",
      newClient: "New API client",
      tabs: {
        clients: "API clients",
        webhooks: "Webhooks",
        deadLetter: "Dead-letter queue",
      },
      clientId: "Client ID",
      scopes: "Scopes",
      lastUsed: "Last used",
      webhooksTitle: "Webhook subscriptions",
      endpoint: "Endpoint",
      events: "Events",
      paused: "Paused",
      lastDelivery: "Last delivery",
      failures: "Failures",
      deadLetterTitle: "Dead-letter messages",
      topic: "Topic",
      eventId: "Event ID",
      attempts: "Attempts",
      nextAttempt: "Next attempt",
    },
    users: {
      title: "User Management",
      subtitle: "Invite users, assign roles, manage MFA and sessions.",
      invite: "Invite user",
      list: "Users",
      mfa: "MFA",
      on: "On",
      off: "Off",
      roles: "Roles",
      lastLogin: "Last login",
      cancelInvitation: "Cancel invitation",
      suspend: "Suspend",
      activate: "Activate",
    },
    roles: {
      title: "Roles & Permissions",
      subtitle:
        "Define roles, permission matrix, data scopes, and field-level permissions.",
      create: "Create role",
      list: "Roles",
      role: "Role",
      system: "System",
      custom: "Custom",
      permissions: "Permissions",
      members: "Members",
      sodConflicts: "SoD conflicts",
    },
    workflows: {
      title: "Workflow Builder",
      subtitle:
        "Design state machines with conditions, approvals, SLA, and versioning.",
      validate: "Validate",
      simulate: "Simulate",
      publish: "Publish version",
      states: "States",
      active: "Active",
      draft: "Draft",
      versionBadge: "{{state}} v{{version}}",
      initial: "initial",
      final: "final",
      transitions: "Transitions",
      auto: "auto",
    },
    approvalMatrix: {
      title: "Approval Matrix Designer",
      subtitle:
        "Configure thresholds, approver roles, delegation, and escalation.",
      validate: "Validate",
      activate: "Activate version",
      /** The name GET …/current gives the matrix when none is defined yet. */
      unnamed: "New approval matrix",
      entity: "Entity: {{entity}}",
      roleStep: "Role: {{role}}",
      counterSignatures: "Counter-signatures required: {{count}}",
      escalation: "Escalation rules",
      afterHours: "After (hours)",
      escalateTo: "Escalate to",
      slaBreach: "SLA breach",
    },
    entityBuilder: {
      title: "Entity Builder",
      subtitle:
        "Create custom business entities (JSONB-backed) with fields, options, validation, and activation — no code, no runtime DDL.",
      validateActivate: "Validate & Activate",
      newEntity: "New custom entity",
      entityName: "Entity name",
      entityNameHint: "e.g. {{example}}",
      whyBlocked: "Why is this blocked?",
      invalidName:
        "Entity names must start with a letter and contain only letters, digits, and underscores.",
      reservedName:
        "“{{name}}” is a reserved core name and cannot be used for a custom entity.",
      fieldTypes: "Field types",
      list: "Custom entities",
      storage: "Storage",
      fields: "Fields",
    },
    formDesigner: {
      title: "Form / View Designer",
      subtitle:
        "Arrange form sections and tabs, conditional visibility, and list/kanban/calendar views from metadata.",
      tabs: {
        views: "Views",
        layouts: "Layouts",
      },
      viewsTitle: "View definitions",
      view: "View",
      default: "Default",
      layoutsTitle: "Form layouts",
      layout: "Layout",
    },
    countryPacks: {
      title: "Country Pack Manager",
      subtitle:
        "Country packs localize tax systems, calendars, formats, statutory reports, and e-invoicing.",
      list: "Country packs",
      pack: "Country pack",
      /** A country pack named by its country (`code` is country-<ISO>). */
      packName: "{{country}} Country Pack",
      installed: "Installed",
      latest: "Latest",
      update: "Update",
      available: "Available",
      current: "Current",
      install: "Install",
      upgrade: "Upgrade",
      disable: "Disable",
      /**
       * FIX-CERT: each pack's certification state, as a label, with the
       * explanation one click away (Decision 230's wording).
       */
      review: "Review",
      reviewState: {
        Default: "Default setting - review recommended",
        Certified: "Certified",
      },
      /** A row the API sent without a state. */
      reviewUnknown: "Not stated",
      explain: {
        title: "{{pack}}: what this label means",
        status: "Status",
        Default:
          "Default setting - review recommended. {{product}} applies these standard-based defaults so the workspace can start. They have not been reviewed by a licensed accountant.",
        Certified:
          "Marked certified. No reviewer and no review date are recorded for it.",
        reviewer: "Reviewed by",
        notReviewed: "Not reviewed by a licensed accountant",
        date: "Date",
        notRecorded: "Not recorded",
        sources: "Source",
        noSources: "This pack's content states no source.",
        responsibility: "Management's responsibility",
        responsibilityText:
          "Management is responsible for reviewing these defaults, for changing any that do not fit the business, and for the figures the workspace reports.",
        close: "Close",
        /** Parts of a pack's content, by their place in it ("." as "_"). */
        parts: {
          accounting: "Accounting",
          accounting_costing: "Costing methods",
          agingBuckets: "Aging bands",
          payrollAccounting: "Payroll accounts",
          tax: "Tax codes",
        },
        /** A part's own word for its state, and for its basis. */
        words: {
          uncertified: "Uncertified",
          certified: "Certified",
          "standard-based": "Standard-based",
        },
      },
    },
    industryPacks: {
      title: "Industry Pack Manager",
      subtitle:
        "Install industry packs to pre-load entities, workflows, dashboards, and posting rules for your sector.",
      browse: "Browse marketplace",
      list: "Industry packs",
      pack: "Industry pack",
      entities: "Entities",
      workflows: "Workflows",
      notInstalled: "Not installed",
      install: "Install",
      manage: "Manage",
    },
    integrations: {
      title: "Integration Center",
      subtitle:
        "Connect banks, payment gateways, e-commerce, tax authorities, e-invoicing, and more.",
      add: "Add connector",
      list: "Connectors",
      connector: "Connector",
      successRate: "Success rate",
      lastRun: "Last run",
      configure: "Configure",
      settings: "Settings",
    },
    dataImport: {
      title: "Data Import Center",
      subtitle:
        "Import master data and opening balances with mapping, validation, dry-run, and reconciliation.",
      template: "Download template",
      new: "New import",
      list: "Import history",
      rows: "Rows",
      success: "Success",
      errors: "Errors",
      started: "Started",
      open: "Open",
    },
    tenant: {
      title: "Tenant Settings",
      subtitle: "Company details, localization, and data export.",
      save: "Save changes",
      environment: "Environment: {{value}}",
      status: "Status: {{value}}",
      plan: "Plan: {{value}}",
      company: "Company",
      companyName: "Company name",
      tenantCode: "Tenant code",
      localization: "Localization",
      locale: "Locale",
      region: "Data residency region",
      isolation: "Isolation mode",
      backup: "Data export",
      exportAll: "Export full data",
    },
    security: {
      title: "Security Center",
      subtitle: "Audit logs, active sessions, API keys, and anomaly alerts.",
      tabs: {
        audit: "Audit log",
        sessions: "Sessions",
      },
      events: "Security events",
      time: "Time",
      sessionsTitle: "Active sessions",
      revokeAll: "Revoke all",
      thisSession: "This session",
      revoke: "Revoke",
    },
    billing: {
      title: "Billing / Subscription",
      subtitle: "Plan, usage, invoices, and payment method.",
      compare: "Compare plans",
      plan: "Plan",
      users: "Users",
      apiRequests: "API requests",
      paymentMethod: "Payment method",
      none: "None on file",
      invoices: "Invoices",
    },
  },
} as const;
